<template lang="pug">
    div.conversation-container(ref="conversationContainer")
        div.text-center.my-2(v-if="loadingConversations")
            v-progress-circular(
                :size='25'
                :width='2'
                color='var(--volie-blue)'
                indeterminate
            )
        v-list.conversation-list
            v-list-item.mb-8(
                    v-for='(conversation, i) in conversationGroupConversations'
                    :key='i'
                    style="padding: 0px; transform: translateY(-11px);"
                )
                v-list-item-subtitle.mt-2.text-center.cursor-default {{ conversation.friendly_created_at }}
                    v-list
                        v-list-item(v-for="(message, index) in conversation.messages" :key="message.id")
                            Message(
                                :message="message"
                                :is-group-chat="isGroupChat"
                                :loading-conversations="loadingConversations"
                                :is-last-message="isLastMessage(index, conversation.messages)"
                                :is-last-conversation="isLastConversation(i)"
                            )
                        v-list-item(
                            v-show="isLastConversation(i)"
                            v-for="(message, index) in tempMessages" 
                            :key="index"
                            )
                            Message(
                                :message="message"
                                :is-temp-message="true"
                                :is-last-message="true"
                                :is-last-conversation="true"
                            )
</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from '@/store'

//Data
import { GetConversationGroupConversations, ReadAllConversationGroupUnreadMessages } from '@/data';

//Components
import Message from '@/components/in_app_chat/message.vue';

export default Vue.extend({
    data() {
        return {
            bus: VolieState.websocketClient().eventBus(),
            conversationGroupConversations: [],
            initialLoad: true,
            lastConversationID: 0,
            loadingConversations: false,
            readyToLazyLoadPreviousConversations: false,
            tempMessages: [],
        }
    },
    methods: {
        getConversationGroupConversations() {
            
            if (!(this.conversationGroupId > 0) || !this.currentUser) {
                return;
            }
            
            if (this.lastConversationID < 0) {
                return;
            }
            
            if (this.loadingConversations) {
                return;
            }
                
            let prevScrollHeight = 0;
            const container = this.scrollableContainer;
            if (container) {
                prevScrollHeight = container.scrollHeight;
            }
                    
            this.loadingConversations = true;

            const options = {
                last_conversation_id: this.lastConversationID || 0,
                conversation_group_id: this.conversationGroupId,
                customer_id: this.currentUser.user_id,
            }

            GetConversationGroupConversations(options)
                .then((response) => {
                    if (response?.Data?.conversations?.length > 0) {
                        if (!(this.conversationGroupConversations?.length > 0)) {
                            this.conversationGroupConversations = response?.Data?.conversations;
                        } else {
                            // Add conversations to beginning of array (loading previous conversations)
                            this.conversationGroupConversations.unshift(...response.Data.conversations);
                        }

                        if (response?.Data?.conversations?.length === 1) {
                            this.lastConversationID = -1;
                        } else {
                            this.lastConversationID = this.conversationGroupConversations[0].conversation_id;
                        }

                        Vue.nextTick(() => { 
                            // if list container is found, hold scroll position in list
                            if (container) {
                                let scrollTo = container.scrollHeight - prevScrollHeight;
                                if (scrollTo > 75) {
                                    // scroll up a little to show a little bit of loaded previous messages and hold position from progress loader
                                    scrollTo = scrollTo - 55;
                                }
                                container.scrollTop = scrollTo;
                            }

                            this.onScrollConversationMessages();
                        });
                    } else {
                        // When all conversations have been loaded
                        this.lastConversationID = -1
                    }

                })
                .catch((error) => {
                    console.error('error getting conversation groups:', error);
                })
                .finally(() => {
                    this.loadingConversations = false;

                    if (this.initialLoad) {
                        this.$nextTick(() => {
                            this.scrollToBottom();
                        });
                        this.initialLoad = false;
                    }
                });
        },
        handleTempMessage() {
            if (this.messageSending === true) {
                this.tempMessages.push({
                    body: this.newMessageBody,
                    last_message_status: 'Sending...',
                    user_full_name: "",
                    personable_id: this.currentUser.user_id,
                    friendly_created_at: "",
                    message_medias: this.newMessageMedias,
                });
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            }
        },
        isLastConversation(index: number) {
            return index === this.conversationGroupConversations.length - 1;
        },
        isLastMessage(index: number, array: any[]) {
            return index === array.length - 1;
        },
        onNewReadReceipt(response: any) {
            if (response?.Data) {
                const data = response?.Data;
                if (data.conversation_group_id !== this.conversationGroupId) {
                    return;
                }

                // find matching conversation
                const conversationIdx = this.conversationGroupConversations.findIndex(
                    i => i.conversation_id === data.conversation_id
                );

                if (conversationIdx < 0) {
                    return;
                }

                const conversation = this.conversationGroupConversations[conversationIdx];

                // update last message status on most recent message
                const lastMsg = conversation.messages[conversation.messages.length - 1];
                if (lastMsg) {
                    lastMsg.last_message_status = data.message_status;
                }
    }
        },
        onNewMessage(response: any) {
            if (response?.Data) {
                const message = response?.Data;
                
                //ignore if not for this conversation group
                if (this.conversationGroupId !== message.conversation_group_id) {
                    return;
                }

                //find conversation
                const conversationIdx = this.conversationGroupConversations.findIndex(i => i.conversation_id === message.conversation_id);

                // If the conversation already exists, append the message to the conversation
                if (conversationIdx >= 0) {
                    
                    // Init conversation and either push message onto array of messages or init a new array of messages
                    const conversation = this.conversationGroupConversations[conversationIdx];
                    if (!conversation || !(conversation.conversation_id > 0)) {
                        return;
                    }

                    // Set messages to empty array if null
                    if (!conversation.messages && !(conversation.messages?.length > 0)) {
                        conversation.messages = [];
                    }

                    // Verify message isn't already in array
                    const messageIdx = conversation.messages.findIndex(i => i.message_id === message.message_id);
                    if (messageIdx >= 0) {
                        return;
                    }

                    // Push message onto array and sort
                    conversation.messages.push(message);
                    this.sortConversationMessages(conversation);

                } else { // Since the message belongs to a new conversation, setup conversation and append message

                    // Init new conversation from message
                    const conversation = {
                        conversation_id: message.conversation_id,
                        conversation_key: message.conversation_key,
                        conversation_group_id: message.conversation_group_id,
                        conversation_group_person_id: message.conversation_group_person_id,
                        created_at: message.conversation_created_at,
                        friendly_created_at: message.conversation_friendly_created_at,
                        is_active: true,
                        last_message_id: message.message_id,
                        last_message_status: message.last_message_status || '',
                        messages: [message],
                    };

                    // Push conversation onto array and sort
                    this.conversationGroupConversations.push(conversation);
                    this.sortConversationGroupConversations();
                }
                
                this.readAllConversationGroupUnreadMessages(this.conversationGroupId, this.currentUser.activated_account_id);
                this.tempMessages.shift();
                this.$nextTick(() => {
                    this.readyToLazyLoadPreviousConversations = true;
                    this.onScrollConversationMessages();
                    this.scrollToBottom();
                });
            }
        },
        onScrollConversationMessages() {
            if (this.loadingConversations || !this.readyToLazyLoadPreviousConversations) {
                return;
            }

            const container = this.scrollableContainer;

            if (!container) {
                return;
            }

            // Trigger load if scrolled near the top (within 25px)
            if (container.scrollTop <= 50) {
                this.readyToLazyLoadPreviousConversations = false;
                this.getConversationGroupConversations();
            }
        },
        scrollToBottom() {
            if (this.scrollableContainer) {
                this.scrollableContainer.scrollTop = this.scrollableContainer.scrollHeight;
            }
        },
        sortConversationGroupConversations() {
            if (this.conversationGroupConversations?.length > 0) {
                this.conversationGroupConversations = this.conversationGroupConversations.sort(function(x, y) {
                    return x.conversation_id - y.conversation_id;
                });
                
                // Set last conversation id
                this.lastConversationID = this.conversationGroupConversations[0].conversation_id;
            }
        },
        sortConversationMessages(conversation) {
            if (conversation?.conversation_id > 0 && conversation?.messages?.length > 0) {
                conversation.messages = conversation.messages.sort(function(x, y) {
                    return x.message_id - y.message_id;
                });
            }
        },
        readAllConversationGroupUnreadMessages(conversationGroupID, accountID) {
            const options = {
                account_id: accountID,
                conversation_group_id: conversationGroupID,
            }

            ReadAllConversationGroupUnreadMessages(options).then((response) => {
                if (response?.Error && response?.Error !== 'Zero unread messages') {
                    console.error('error reading unread messages:', response?.Error);
                }
            })
            .catch((error) => {
                console.error('error caught when reading unread messages:', error);
            });

            return;
        },
    },
    computed: {
        currentUser() {
            return VolieState.currentUser();
        },
    },
    components: {
        Message,
    },
    props: {
        conversationGroupId: {
            type: Number,
            required: true,
        },
        isGroupChat: Boolean,
        messageSending: Boolean,
        newMessageBody: String,
        newMessageMedias: Array,
        scrollableContainer: HTMLElement
    },
    watch: {
        newMessageBody(newVal) {
            if (newVal?.length > 0) {
                this.handleTempMessage();
            }
        },
        newMessageMedias(newVal) {
            if (newVal?.length > 0 && this.newMessageBody?.length === 0) {
                this.handleTempMessage();
            }
        },
    },
    created() {
        this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "newUserToUserMessage" }}, this.onNewMessage);
        this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "userToUserMessageReadReceipt" }}, this.onNewReadReceipt);
    },
    mounted() {
        this.readyToLazyLoadPreviousConversations = true;
        this.getConversationGroupConversations();
    },
    destroyed() {
        this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "newUserToUserMessage" }}, this.onNewMessage);
        this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "userToUserMessageReadReceipt" }}, this.onNewReadReceipt);
    },
})
</script>

<style lang="scss" scoped>
    .conversation-list, :deep(.v-list), :deep(.v-list-item) {
        width: 100%;
        padding-bottom: 0;
    }

    .conversation-container {
        width: 100%;
    }
</style>